import CodeMirror from 'codemirror'
import {
    CUSTOM_FIELD_DATA_TYPES,
    type CustomFieldDefinition,
    type CustomFieldEditorContext,
    customFieldDataTypeName,
} from './types'

export const CUSTOM_FIELD_REFERENCE_PATTERN = /\bcustom\.cf_[a-z0-9_]+\b/g
const CUSTOM_FIELD_TOKEN_PATTERN = /^custom\.[A-Za-z0-9_]+/

export type CustomFieldReferenceResolution =
    | { status: 'resolved'; definition: CustomFieldDefinition }
    | { status: 'deleted'; definition: CustomFieldDefinition }
    | { status: 'wrong-keyspace'; definition: CustomFieldDefinition }
    | { status: 'unknown' }

export const resolveCustomFieldReference = (
    reference: string,
    context?: CustomFieldEditorContext,
): CustomFieldReferenceResolution => {
    const key = reference.startsWith('custom.') ? reference.slice('custom.'.length) : reference
    const definition = context?.definitions.find((candidate) => candidate.key === key)

    if (!definition) return { status: 'unknown' }
    if (definition.deleted_at) return { status: 'deleted', definition }
    if (!context?.expectedEntityType || definition.entity_type !== context.expectedEntityType) {
        return { status: 'wrong-keyspace', definition }
    }
    return { status: 'resolved', definition }
}

export const customFieldTokenStyle = (resolution: CustomFieldReferenceResolution): string => {
    if (resolution.status !== 'resolved') return 'custom-field-error'

    switch (resolution.definition.data_type) {
        case CUSTOM_FIELD_DATA_TYPES.Number:
            return 'custom-field-number'
        case CUSTOM_FIELD_DATA_TYPES.Boolean:
            return 'custom-field-boolean'
        case CUSTOM_FIELD_DATA_TYPES.String:
            return 'custom-field-string'
    }
}

/** Consumes one complete custom reference from the stream, including broken references. */
export const consumeCustomFieldToken = (
    stream: CodeMirror.StringStream,
    context?: CustomFieldEditorContext,
): string | null => {
    const match = stream.string.slice(stream.pos).match(CUSTOM_FIELD_TOKEN_PATTERN)
    if (!match) return null

    stream.match(match[0])
    return customFieldTokenStyle(resolveCustomFieldReference(match[0], context))
}

export interface CustomFieldCompletion {
    text: string
    displayText: string
    className: string
}

export const buildCustomFieldCompletions = (
    query: string,
    context?: CustomFieldEditorContext,
): CustomFieldCompletion[] => {
    if (!context?.expectedEntityType) return []

    const normalizedQuery = query
        .trim()
        .toLocaleLowerCase()
        .replace(/^custom\./, '')
    return context.definitions
        .filter(
            (definition) =>
                !definition.deleted_at && definition.entity_type === context.expectedEntityType,
        )
        .filter((definition) => {
            if (!normalizedQuery) return true
            return (
                definition.name.toLocaleLowerCase().includes(normalizedQuery) ||
                definition.key.toLocaleLowerCase().includes(normalizedQuery)
            )
        })
        .sort((left, right) => left.name.localeCompare(right.name))
        .map((definition) => ({
            text: `custom.${definition.key}`,
            displayText: `${definition.name} (${customFieldDataTypeName(definition.data_type)})`,
            className: `cm-${customFieldTokenStyle({ status: 'resolved', definition })}`,
        }))
}

const marksByEditor = new WeakMap<CodeMirror.Editor, CodeMirror.TextMarker[]>()

const clearDecorations = (editor: CodeMirror.Editor) => {
    marksByEditor.get(editor)?.forEach((mark) => mark.clear())
    marksByEditor.delete(editor)
}

const buildWidget = (definition: CustomFieldDefinition): HTMLElement => {
    const widget = document.createElement('span')
    const typeName = customFieldDataTypeName(definition.data_type)
    widget.className = `cm-custom-field-widget cm-${customFieldTokenStyle({
        status: 'resolved',
        definition,
    })}`
    const label = document.createElement('span')
    label.textContent = `custom.${definition.name}`
    widget.appendChild(label)
    widget.tabIndex = 0
    widget.dataset.customFieldKey = definition.key
    widget.dataset.customFieldType = typeName

    const metadata = [
        definition.name,
        definition.description,
        `Type: ${typeName}`,
        `Key: ${definition.key}`,
        `Updated: ${definition.updated_at}`,
    ].filter(Boolean)
    widget.title = metadata.join('\n')
    widget.setAttribute('aria-label', metadata.join('. '))

    const tooltip = document.createElement('span')
    tooltip.className = 'cm-custom-field-tooltip'
    tooltip.setAttribute('role', 'tooltip')
    tooltip.append(`${definition.name}\n${definition.description ?? ''}\nType: ${typeName}\nKey: `)
    const key = document.createElement('code')
    key.textContent = definition.key
    tooltip.appendChild(key)
    tooltip.append(`\nUpdated: ${definition.updated_at}`)
    widget.appendChild(tooltip)

    return widget
}

/**
 * Replaces only resolved references. Broken references stay as raw document
 * text so users can see and copy the key that needs repairing.
 */
export const applyCustomFieldDecorations = (
    editor: CodeMirror.Editor,
    context?: CustomFieldEditorContext,
): (() => void) => {
    clearDecorations(editor)
    if (!context) return () => undefined

    const marks: CodeMirror.TextMarker[] = []
    editor.eachLine((lineHandle) => {
        const lineNumber = editor.getLineNumber(lineHandle)
        if (lineNumber === null) return

        const line = lineHandle.text
        const referencePattern = new RegExp(CUSTOM_FIELD_REFERENCE_PATTERN.source, 'g')
        let match: RegExpExecArray | null
        while ((match = referencePattern.exec(line)) !== null) {
            const resolution = resolveCustomFieldReference(match[0], context)
            if (resolution.status !== 'resolved') continue

            // Decoration is a presentation layer over semantic tokens. Do not
            // turn text inside a string or comment into a custom-field widget.
            // The fallback keeps lightweight editor fakes usable in tests.
            const expectedStyle = customFieldTokenStyle(resolution)
            const tokenType = editor.getTokenTypeAt?.(CodeMirror.Pos(lineNumber, match.index + 1))
            if (tokenType && !tokenType.split(/\s+/).includes(expectedStyle)) continue

            marks.push(
                editor.markText(
                    CodeMirror.Pos(lineNumber, match.index),
                    CodeMirror.Pos(lineNumber, match.index + match[0].length),
                    {
                        atomic: true,
                        replacedWith: buildWidget(resolution.definition),
                    },
                ),
            )
        }
    })
    marksByEditor.set(editor, marks)

    return () => clearDecorations(editor)
}
